import { useMemo } from "react";

import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import Button from "@mui/material/Button";
import Stack from "@mui/material/Stack";

import { budgetTxt } from "../../../assets/texts/CloudAnalytics/budget";
import { EditableResourceHeader } from "../../../Components/EditableResourceHeader/EditableResourceHeader";
import { useGotoParentUrl } from "../../../Components/hooks/useGotoParentUrl";
import { ToolTipWrapper } from "../../../Components/TooltipWrapper";
import { useAuthContext } from "../../../Context/AuthContext";
import { useCustomerContext } from "../../../Context/CustomerContext";
import { type Budget } from "../../../types";
import { userAccess } from "../utilities";
import { useErrorSnackbar, useSuccessSnackbar } from "./../../../Components/SharedSnackbar/SharedSnackbar.context";
import { updateBudget } from "./db";
import { useBudgetInvestigateHandler, useEditBudgetHandler } from "./hooks";

export const ViewBudgetHeader = ({ budget }: { budget: Budget }) => {
  const { customer } = useCustomerContext();
  const { currentUser } = useAuthContext({ mustHaveUser: true });
  const handleInvestigateBudget = useBudgetInvestigateHandler({ budget, openInNewTab: true });
  const handleEdit = useEditBudgetHandler({ budget });
  const successSnackbar = useSuccessSnackbar();
  const errorSnackbar = useErrorSnackbar();

  const { editor: isCurrentUserEditor, owner: isCurrentUserOwner } = userAccess(currentUser.email, budget.data);
  const currentUserCanEdit = isCurrentUserOwner || isCurrentUserEditor;

  const parentUrl = useMemo(() => `/customers/${customer.id}/analytics/budgets`, [customer.id]);
  const handleBack = useGotoParentUrl(parentUrl);

  const handleSave = async (values: { name: string; description?: string }) => {
    try {
      await updateBudget({
        ref: budget.ref,
        name: values.name,
        description: values.description || "",
        config: budget.data.config,
      });
      successSnackbar(budgetTxt.SUCCESSFULLY_SAVED);
    } catch (error) {
      errorSnackbar(budgetTxt.FAILED_TO_UPDATE);
      throw error;
    }
  };

  return (
    <Stack
      direction={{ xs: "column", md: "row" }}
      sx={{
        alignItems: { xs: "flex-start", md: "center" },
        mb: 3,
        mt: 6,
        justifyContent: "space-between",
        width: "100%",
        minWidth: 0,
      }}
    >
      <EditableResourceHeader
        name={budget.data.name}
        description={budget.data.description}
        onSave={handleSave}
        handleBack={handleBack}
        disabled={!currentUserCanEdit}
        resourceName="budget"
      />
      <Stack
        direction="row"
        sx={{
          alignItems: "center",
          gap: 2,
          alignSelf: { xs: "flex-end" },
          "& .MuiButton-root": {
            whiteSpace: "nowrap",
          },
        }}
      >
        <Button startIcon={<OpenInNewIcon />} onClick={handleInvestigateBudget}>
          {budgetTxt.OPEN_IN_REPORT}
        </Button>
        <ToolTipWrapper title={currentUserCanEdit ? "" : budgetTxt.NO_EDIT_PERMISSION_TOOLTIP}>
          <Button variant="outlined" onClick={handleEdit} disabled={!currentUserCanEdit}>
            {budgetTxt.EDIT_BUDGET}
          </Button>
        </ToolTipWrapper>
      </Stack>
    </Stack>
  );
};
